import { logger } from '@tunasong/models'
import { useDeviceState } from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import type { AudioController, AudioControllerGainEvent } from './audio-controller.js'
import { AudioEngineContext } from './audio-engine-context.js'
import { BusController } from './bus.js'
import { AudioEngine } from './engine.js'

interface AudioContainerProps {
  engine: AudioEngine | null
  children?: React.ReactNode
}

/** Local mixer settings. */
interface LocalMixerSetting {
  gain: number
}
interface LocaMixerSettings {
  input?: Record<string, LocalMixerSetting>
  bus?: Record<string, LocalMixerSetting>
}

export const AudioContainer: FC<AudioContainerProps> = ({ children, engine }) => {
  const [mixerSettings, setMixerSettings, localMixerLoaded] = useDeviceState<LocaMixerSettings>('mixer-settings')

  /**
   * The engine initializes its audio stack lazily (GH #1542). Accessing
   * `engine.mixer` forces that expensive initialization, so the mixer-settings
   * effects below must wait until the engine reports it is initialized.
   */
  const [audioInitialized, setAudioInitialized] = useState(Boolean(engine?.initialized))
  useEffect(() => {
    if (!engine || audioInitialized) {
      return
    }
    if (engine.initialized) {
      setAudioInitialized(true)
      return
    }
    const handleInitialized = () => setAudioInitialized(true)
    engine.addEventListener('initialized', handleInitialized)
    return () => {
      engine.removeEventListener('initialized', handleInitialized)
    }
  }, [engine, audioInitialized])

  /** Handle start/resume of audio context */
  useEffect(() => {
    if (!engine || engine?.started) {
      return
    }

    const start = async (ev: Event) => {
      if (engine.started) {
        return
      }
      await engine.start()
      logger.debug(`*** AudioEngine started from event ${ev.type} ***`)
    }

    document.addEventListener('click', start)
    document.addEventListener('touch', start)
    return () => {
      document.removeEventListener('click', start)
      document.removeEventListener('touch', start)
    }
  }, [engine])

  /** Handle local settings */
  useEffect(() => {
    if (!engine || !audioInitialized) {
      return
    }

    const handleVolume = (ev: Event) => {
      const gainEvent = ev as CustomEvent<AudioControllerGainEvent>
      const gain = gainEvent.detail.gain
      const controller = ev.target as AudioController
      const type = controller instanceof BusController ? 'bus' : 'input'

      const settingsForType = mixerSettings?.[type]
      const newSettings = {
        ...mixerSettings,
        [type]: {
          ...settingsForType,
          [controller.name]: {
            ...settingsForType?.[controller.name],
            gain,
          },
        },
      } satisfies LocaMixerSettings
      setMixerSettings(newSettings)
    }
    // Use the abort controller to cancel the event listeners when the component unmounts
    const abortController = new AbortController()

    // For each of the channels and busses, add a listener for volume changes
    for (const input of engine.mixer.channels) {
      input.addEventListener('gain', handleVolume, { signal: abortController.signal })
    }
    for (const bus of engine.mixer.busses) {
      bus.addEventListener('gain', handleVolume, { signal: abortController.signal })
    }
    return () => {
      // Remove all the listeners
      abortController.abort()
    }
  }, [engine, audioInitialized, setMixerSettings, mixerSettings])

  // Initial load of mixer settings from local state
  const mixerInitialized = useRef(false)
  useEffect(() => {
    if (!engine || !audioInitialized || mixerInitialized.current || !localMixerLoaded || !mixerSettings) {
      return
    }

    for (const [name, settings] of Object.entries(mixerSettings.input ?? {})) {
      const channel = engine.mixer.getChannelByName(name)
      if (!channel) {
        logger.warn('Channel not found', name)
        continue
      }
      channel.gain = settings.gain
    }
    for (const [name, settings] of Object.entries(mixerSettings.bus ?? {})) {
      const bus = engine.mixer.getBusByName(name)
      if (!bus) {
        logger.warn('Bus not found', name)
        continue
      }
      bus.gain = settings.gain
    }

    mixerInitialized.current = true
  }, [engine, audioInitialized, localMixerLoaded, mixerSettings])

  return <AudioEngineContext.Provider value={engine}>{children}</AudioEngineContext.Provider>
}

export default AudioContainer
